"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.componentsSerializer = exports.componentsDeserializer = void 0;
const code_renderer_1 = require("./utils/code-renderer");
const common_utils_1 = require("./utils/common-utils");
const sbs_layouts_1 = require("./utils/sbs-layouts");
const informative_utils_1 = require("./utils/informative-utils");
const componentsDeserializer = ($, locale) => ({
    code: ($node) => {
        const decodedCode = (0, code_renderer_1.decodeEntity)($node.find('code').text());
        const lang = $node.attr('data-language');
        const legacyTypes = ['markup', 'text'];
        return {
            language: legacyTypes.indexOf(lang) !== -1 ? 'html' : lang,
            lineNumbers: $node.attr('data-line-numbers') === 'true',
            lineWrapping: $node.attr('data-line-wrapping') === 'true',
            code: decodedCode,
        };
    },
    collapsible: ($node) => {
        const title = $node.find('.collapsible-title').text();
        const htmlContent = $node.find('.collapsible-content').html();
        return {
            title,
            html: (0, common_utils_1.convertTrixImagesToSimpleImgTags)(htmlContent, $),
        };
    },
    heading: ($node) => ({
        text: $node.text(),
        anchorId: $node.attr('id') || '',
        level: parseInt($node.attr('level'), 10) || 3,
    }),
    html: ($node) => ({
        html: $('<div>').append($node).html() || '',
    }),
    iframe: ($node) => {
        const $iframe = $node.find('iframe');
        const width = $iframe.css('width').indexOf('%') === -1 ? parseInt($iframe.css('width'), 10) : null;
        const height = $iframe.css('height').indexOf('%') === -1 ? parseInt($iframe.css('height'), 10) : null;
        return {
            url: $iframe.attr('src'),
            width: width,
            height: height,
            align: ($node.css('text-align') || 'center'),
            scrolling: $iframe.attr('scrolling') || 'auto',
        };
    },
    image: ($node) => {
        const $img = $node.is('img') ? $node : $node.find('img');
        const $link = $node.find('a');
        return {
            src: $img.attr('src'),
            alt: $img.attr('alt') || '',
            link: $link.attr('href') || '',
            align: 'center',
        };
    },
    informative: ($node) => {
        const maybeType = $node.attr('info-type');
        let color = 'green';
        if (maybeType) {
            color = (0, informative_utils_1.informativeTypeToColor)(maybeType);
        }
        else {
            $node.toArray().forEach((elem) => {
                const $elem = $(elem);
                if ($elem.attr('info-color')) {
                    color = $elem.attr('info-color');
                }
            });
        }
        const title = maybeType
            ? (0, informative_utils_1.informativeTypeToTitle)(maybeType, locale)
            : $node.find('.info-title')[0]
                ? $node.find('.info-title').text()
                : null;
        const htmlContent = $('.info-content', $node).html();
        return {
            html: (0, common_utils_1.convertTrixImagesToSimpleImgTags)(htmlContent, $),
            color,
            title,
        };
    },
    line: ($node) => ({
        style: ($node.attr('line-style') || 'thin'),
    }),
    markdown: ($node) => {
        const oldHtmlNodeToValue = (n) => {
            let encodedMarkdown = n.find('.encoded-markdown');
            if (encodedMarkdown.toArray().length === 0) {
                return null;
            }
            encodedMarkdown = encodedMarkdown.text();
            const decodedMarkdown = atob(encodedMarkdown);
            return { markdown: decodedMarkdown, renderedHtml: '' };
        };
        const oldValue = oldHtmlNodeToValue($node);
        if (oldValue === null || oldValue === void 0 ? void 0 : oldValue.markdown) {
            return oldValue;
        }
        const originalMarkdown = $node.find('.original-markdown').html();
        const originalMarkdownDecoded = (0, code_renderer_1.decodeEntity)(originalMarkdown);
        const renderedHtmlNode = $node.find('.markdown-rendered-html');
        const renderedHtml = renderedHtmlNode ? renderedHtmlNode.html() : '';
        return {
            markdown: originalMarkdownDecoded,
            renderedHtml,
        };
    },
    'step-by-step': ($node) => {
        var _a;
        const steps = $node
            .find('.step')
            .toArray()
            .map((step) => {
            const $step = $(step);
            const $img = $step.find('.step-image');
            const $desc = $step.find('.step-description');
            const $title = $step.find('.step-title');
            return {
                html: $desc.html(),
                title: $title.text(),
                image: {
                    src: $img.attr('src'),
                    alt: $img.attr('alt') || '',
                    link: $img.attr('href') || '',
                },
            };
        });
        const sbsLayout = (((_a = sbs_layouts_1.stepByStepLayouts.find((l) => l.key === $node.attr('data-layout'))) === null || _a === void 0 ? void 0 : _a.key)
            || sbs_layouts_1.stepByStepLayouts[0].key);
        return {
            layout: sbsLayout,
            allowCircular: $node.attr('data-allow-circular') === 'true',
            steps,
        };
    },
    table: ($node) => {
        const headers = $node.find('th').toArray();
        const hasOverridenWidth = headers.some((header) => !!$(header).data('column-width'));
        const columnWidths = hasOverridenWidth
            ? headers.map((elm) => +$(elm).data('column-width'))
            : undefined;
        return {
            rows: $node.find('tbody tr').length,
            columns: headers.length,
            headings: headers.map((elm) => $(elm).html()),
            cells: $node
                .find('tbody tr')
                .toArray()
                .map((tr) => $(tr)
                .find('td')
                .toArray()
                .map((td) => $(td).html())),
            columnWidths,
        };
    },
    tabs: ($node) => {
        const tabsValue = {
            headings: [],
            contents: [],
            slugs: [],
            count: 0,
            recommendedTab: undefined,
        };
        $node.find('.tab-heading')
            .toArray()
            .forEach((heading, index) => {
            const headingClasses = $(heading).attr('class').split(' ');
            if (headingClasses.includes('recommended')) {
                tabsValue.recommendedTab = index;
            }
            tabsValue.headings[index] = $(heading).text();
            tabsValue.slugs[index] = String($(heading).data('slug'));
        });
        $node.find('.tab-content')
            .toArray()
            .forEach((tabContentComponents, tabIndex) => {
            tabsValue.contents[tabIndex] = Array.from(tabContentComponents.children).map((component) => {
                const type = $(component).attr('data-component-type');
                const value = (0, exports.componentsDeserializer)($, locale)[type]($(component).contents());
                return { type, value, key: '' };
            });
        });
        tabsValue.count = tabsValue.headings.length;
        return tabsValue;
    },
    text: ($node) => {
        const htmlWithFixedDoubleSpaces = (0, common_utils_1.fixDoubleSpacesInHtml)($node.parent().html());
        return {
            html: (0, common_utils_1.convertTrixImagesToSimpleImgTags)(htmlWithFixedDoubleSpaces, $) || '',
        };
    },
    video: ($node) => {
        const $iframe = $node.find('iframe');
        const width = $iframe.css('width').indexOf('%') === -1 ? parseInt($iframe.css('width'), 10) : null;
        const height = $iframe.css('height').indexOf('%') === -1 ? parseInt($iframe.css('height'), 10) : null;
        return {
            url: $iframe.attr('src'),
            width: width,
            height: height,
            align: $node.css('text-align'),
            invalidUrl: false,
        };
    },
    question: ($node) => ({
        question: $node.text(),
    }),
});
exports.componentsDeserializer = componentsDeserializer;
exports.componentsSerializer = {
    code: (value) => {
        const encodedCode = (0, code_renderer_1.encodeEntity)(value.code);
        return `<div class="code-snippet" data-language="${value.language}" data-line-numbers="${value.lineNumbers}" data-line-wrapping="${value.lineWrapping}"><pre><code>${encodedCode}</code></pre></div>`;
    },
    collapsible: (value) => `<div><h4 class="collapsible-title">${value.title}</h4><div class="collapsible-content">${value.html}</div></div>`,
    heading: (value) => {
        const encoded = (0, code_renderer_1.encodeEntity)(value.text);
        return `<h2 id="${value.anchorId}" level="${value.level}">${encoded}</h2>`;
    },
    html: (value) => value.html,
    iframe: (value) => {
        const width = value.width ? value.width + 'px' : '600px';
        const height = value.height ? value.height + 'px' : '400px';
        return `<div class="iframe-wrapper" style="text-align:${value.align}"><iframe src="${value.url}" style="width:${width};height:${height};" scrolling="${value.scrolling}"></iframe></div>`;
    },
    image: (value) => {
        const imageHtml = `<img src="${value.src}" ${value.alt ? 'alt="' + value.alt + '"' : ''}/>`;
        const innerHtml = value.link ? `<a href="${value.link}">${imageHtml}</a>` : imageHtml;
        return `<div class="img-container" style="text-align:${value.align}">${innerHtml}</div>`;
    },
    informative: (value) => {
        const maybeTitle = value.title !== null ? `<div class="info-title">${value.title}</div>` : '';
        return `<div class="info-container" info-color="${value.color}">${maybeTitle}<div class="info-content">${value.html}</div></div>`;
    },
    line: (value) => `<hr line-style="${value.style}" />`,
    markdown: (value) => {
        const html = value.renderedHtml ? value.renderedHtml : '';
        const originalMarkdownEncoded = (0, code_renderer_1.encodeEntity)(value.markdown);
        return `<div><div class="markdown-rendered-html">${html}</div><div class="original-markdown" style="display: none">${originalMarkdownEncoded}</div></div>`;
    },
    'step-by-step': (value) => {
        const stepsHtml = value.steps
            .map((step) => `<section class="step"><div class="step-title">${step.title}</div><img class="step-image" src="${step.image.src}" href="${step.image.link}" alt="${step.image.alt}" /><div class="step-description">${step.html}</div></section>`)
            .join('');
        const layout = value.layout || 'vertical';
        return `<div class="step-by-step-container" data-layout="${layout}" data-allow-circular="${value.allowCircular}">${stepsHtml}</div>`;
    },
    table: (value) => {
        let headContent = '';
        let bodyContent = '';
        const columnWidths = value.columnWidths;
        for (let col = 0; col < value.columns; col++) {
            const maybeWidthDataAttr = columnWidths ? ` data-column-width=${columnWidths[col]}` : '';
            headContent += `<th${maybeWidthDataAttr}>${value.headings[col] || ''}</th>`;
        }
        for (let row = 0; row < value.rows; row++) {
            bodyContent += '<tr>';
            for (let col = 0; col < value.columns; col++) {
                bodyContent += `<td>${value.cells[row] && value.cells[row][col] ? value.cells[row][col] : ''}</td>`;
            }
            bodyContent += '</tr>';
        }
        return `<div class="table-wrapper"><table><thead><tr>${headContent}</tr><tbody>${bodyContent}</tbody></table></div>`;
    },
    tabs: (value) => {
        let headings = '<div class="headings-wrapper">';
        value.headings.forEach((heading, index) => {
            const isRecommended = value.recommendedTab === index ? 'recommended' : '';
            const slug = value.slugs && value.slugs.length ? value.slugs[index] : '';
            headings += `<div data-slug="${slug}" class="tab-heading heading-${index} ${isRecommended}">${heading}</div>`;
        });
        headings += '</div>';
        let contents = '<div class="tab-contents-wrapper">';
        value.contents.forEach((content) => {
            contents += '<div class="tab-content">';
            contents += content
                .map((component) => {
                const inner = exports.componentsSerializer[component.type](component.value);
                return `<div data-component-type="${component.type}">${inner}</div>`;
            })
                .join('');
            contents += '</div>';
        });
        contents += '</div>';
        return `<div class="tabs-wrapper">${headings}${contents}</div>`;
    },
    text: (value) => (0, common_utils_1.fixDoubleSpacesInHtml)(value.html.trim()),
    video: (value) => {
        const width = value.width ? value.width + 'px' : '100%';
        const height = value.height ? value.height + 'px' : '100%';
        return `<div style="text-align:${value.align}"><span class="video-overlay" style="width:${value.width}px; height:${value.height}px">
		</span><iframe src="${value.url}" style="width:${width};height:${height};"></iframe></div>`;
    },
    question: (value) => {
        const encodedQuestion = (0, code_renderer_1.encodeEntity)(value.question);
        return `<div class="question-component">${encodedQuestion}</div>`;
    },
};
